/** Types for the AIChat component. */
import type {
  AgentExecution,
  AgentExecutionStatus,
} from "@/types/agent-execution";
import type {
  AnalyticsChartType,
  AnalyticsPresentationFields,
  AnalyticsResultType,
} from "@/types/analytics-ai";
import type {
  AskDecodaAttachmentContext,
  AskDecodaResponseBlock,
  AskDecodaRuntimeState,
} from "@/types/chat-thread";
import type { AIFeedbackType } from "@/types/enums";
import type { FileAttachment } from "@/types/file-attachment";
import type { SuggestionItem } from "@/types/suggestions";

export { AnalyticsChartType, AnalyticsResultType } from "@/types/analytics-ai";

export type { FileAttachment } from "@/types/file-attachment";

/** A single data artifact within a multi-artifact response */
export interface AnalyticsArtifactPayload extends AnalyticsPresentationFields {
  execution_time_ms?: number;
  column_types?: Record<string, string>;
  columns: string[];
  rows: unknown[][];
  sql?: string | null;
  explanation?: string;
  chart_type?: AnalyticsChartType;
  python_code?: string | null;
  currency_column_indices?: number[];
}

export interface AnalyticsResultPayload extends AnalyticsPresentationFields {
  column_types?: Record<string, string>;
  explanation: string;
  /** LLM-generated narrative shown above the table (when present) */
  narrative?: string;
  result_type: AnalyticsResultType;
  columns: string[];
  rows: unknown[][];
  message: string | null;
  sql: string | null;
  execution_time_ms: number;
  total?: number;
  page?: number;
  per_page?: number;
  /** Indices of columns that contain currency (for formatCurrency display) */
  currency_column_indices?: number[];
  /** Python code used for analysis (when needs_python) */
  python_code?: string | null;
  /** Search context (tables, columns) gathered when search_context tool ran */
  search_context?: string | null;
  /** Optional hint for visualization (line/bar/pie) */
  chart_type?: AnalyticsChartType;
  /** Chart title */
  chart_title?: string;
  /** X-axis label */
  x_label?: string;
  /** Y-axis label */
  y_label?: string;
  /** For result_type="question": optional clickable options */
  clarifying_options?: string[];
  /** Multiple artifacts when the agent ran multiple queries (grid display) */
  artifacts?: AnalyticsArtifactPayload[];
}

/** Planning step from analytics SSE (query_1, python_1, search_context, etc.) */
export interface AnalyticsStepPayload {
  step: string;
  status: AgentExecutionStatus;
  title?: string;
  elapsed_ms: number;
  sql?: string;
  python_code?: string;
  explanation?: string;
  message?: string;
  reason?: string;
}

export interface SettingsSource {
  kind: "setting";
  sourceId: string;
  targetId: string;
  label: string;
  route?: string | null;
  fieldIds: string[];
  recordIds: string[];
}

export interface ChatSourceBundle {
  version?: 1;
  wikiArticleIds?: string[];
  docPaths?: string[];
  settingsSources?: SettingsSource[];
}

export enum ChatResponseStatus {
  STREAMING = "STREAMING",
  COMPLETED = "COMPLETED",
  BLOCKED = "BLOCKED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

export interface ChatMessage {
  id?: string;
  requestId?: string;
  runtimeState?: AskDecodaRuntimeState | null;
  role: "user" | "assistant" | "system";
  content: string;
  /** Native Ask response body, ordered across text and execution references. */
  responseBlocks?: AskDecodaResponseBlock[] | null;
  /** Response-owned assistant introduction shown before tool activity. */
  preamble?: string | null;
  timestamp: Date;
  feedbackType?: AIFeedbackType | null;
  pendingFeedback?: boolean;
  files?: FileAttachment[];
  /** Persisted lifecycle for this assistant response. */
  responseStatus?: ChatResponseStatus;
  /** False only for a local question the server has not acknowledged yet. */
  requestAccepted?: boolean;
  /** Execution attempts owned by this assistant response. */
  executions?: AgentExecution[];
  isSummary?: boolean; // Indicates this is a summary of previous messages
  /** Analytics table/message result (for analytics chat) */
  analyticsResult?: AnalyticsResultPayload;
  /** Legacy planning steps used by non-Ask-Decoda analytics surfaces. */
  analyticsSteps?: AnalyticsStepPayload[];
  /** Server-validated sources supporting this response. */
  groundingSources?: ChatSourceBundle;
  /** Transient progress/status text delivered over SSE. */
  progressMessage?: string;
}

export type FeedbackType = AIFeedbackType;

export interface AIChatTool {
  name: string;
  description: string;
  callback: (params: Record<string, unknown>) => Promise<string>;
}

export interface FormattingOptions {
  allowedRefs?: string[];
  onCitationClick?: (tab: string, itemId?: string) => void;
  enableBold?: boolean;
  enableItalics?: boolean;
  enableLists?: boolean;
}

/** Canonical SSE event type for all AI chat streams (Ask Decoda agent, patient QA, etc.). */
export interface StreamEvent {
  type:
    | "text"
    | "metadata"
    | "summary"
    | "error"
    | "status"
    | "analytics_step"
    | "analytics_result"
    | "grounding_sources"
    | "model_retry"
    | "validated"
    | "tool_call";
  text?: string;
  /** tool_call: what the agent is looking up, e.g. "appointments" */
  label?: string;
  chatId?: string;
  requestId?: string;
  correctedText?: string;
  attachmentContext?: AskDecodaAttachmentContext;
  summary?: string;
  error?: string;
  message?: string;
  attempt?: number;
  maxAttempts?: number;
  delayMs?: number;
  reason?: string;
  status?: string;
  step?: Record<string, unknown>;
  result?: Record<string, unknown>;
  wikiArticleIds?: string[];
  docPaths?: string[];
  settingsSources?: SettingsSource[];
}

// API function types for AIChat
export interface AIChatAPIFunctions {
  // Stream AI response - takes question, history, files, and model name, returns async generator
  streamResponse: (
    question: string,
    history?: Array<{
      role: "user" | "assistant";
      content: string;
      files?: FileAttachment[];
      analyticsResult?: AnalyticsResultPayload;
    }>,
    files?: FileAttachment[],
    signal?: AbortSignal,
    modelName?: string,
  ) => AsyncIterable<string | StreamEvent>;

  // Generate suggestions - takes history, returns suggestions
  generateSuggestions: (
    history: Array<{ role: "user" | "assistant"; content: string }>,
  ) => Promise<string[]>;
}

export interface AIChatProps {
  // API functions for backend communication
  api: AIChatAPIFunctions;

  // Chat type and context builder for feedback (optional)
  // If provided, AIChat will handle feedback internally using generic endpoints
  chatType?: string; // e.g., "PATIENT_QA"
  // Function that returns context fields needed for this chat type (e.g., {patient_id: "..."})
  getFeedbackContext?: () => Record<string, string>;

  // Optional tool callbacks (for future LangGraph integration)
  tools?: AIChatTool[];

  // Optional callback to build system instructions
  buildInstructions?: () => string;

  // Initial suggestions (if available from summary)
  initialSuggestions?: string[];
  placeholder?: string;

  // Model configuration
  modelName?: string; // AI model to use (defaults to "gemini-3.1-flash-lite")

  // File upload
  showFileUpload?: boolean;
  maxFileSize?: number;
  acceptedFileTypes?: string[];

  // History management
  initialHistory?: ChatMessage[];
  /** Called when conversation history changes (e.g. for persistence) */
  onHistoryChange?: (history: ChatMessage[]) => void;
  /** When set, automatically send this question on mount (e.g. from empty state suggestion) */
  triggerQuestion?: string;

  /** TipTap @-mention catalog (passed to the input; optional async search / gate open) */
  mentionSuggestions?: SuggestionItem[];
  onSearchMentions?: (query: string) => Promise<SuggestionItem[]>;
  onMentionMenuOpen?: () => boolean;

  // Text formatting options (citations, bold, lists, italics)
  formattingOptions?: FormattingOptions;

  /** Render planning/analysis steps (schema, SQL, execute) — during generation or persisted with result */
  renderPlanningSteps?: (
    steps: AnalyticsStepPayload[],
    options?: {
      sql?: string | null;
      pythonCode?: string | null;
      /** Query plan / explanation (what the query does) */
      plan?: string | null;
      /** Search context (tables, columns found) */
      searchContext?: string | null;
      /** When true, the plan starts collapsed (used for persisted results) */
      defaultCollapsed?: boolean;
    },
  ) => React.ReactNode;
}
